import * as React from 'react'
import { CalendarIcon, ChevronLeft, ChevronRight } from 'lucide-react'

import {
  formatCalendarDate,
  parseCalendarDate,
  toCalendarDateString,
} from '@/shared/lib/tenantDateTime'
import { cn } from '@/shared/lib/utils'
import { Button } from '@/shared/ui/button'
import { Popover, PopoverContent, PopoverTrigger } from '@/shared/ui/popover'
import { CalendarGrid, MONTHS_FULL, MONTHS_SHORT } from '@/shared/ui/calendar-grid'

const YEAR_GRID_SIZE = 12

type ViewMode = 'days' | 'months' | 'years'

interface QuickSelectDatePickerInputProps {
  id?: string
  value: string
  onChange: (value: string) => void
  placeholder?: string
  disabled?: boolean
  className?: string
  minDate?: Date
  maxDate?: Date
}

/**
 * Date picker with month/year quick-select.
 * Clicking the header label cycles through day → month → year views,
 * letting users jump to a specific month and year without stepping through arrows.
 */
export function QuickSelectDatePickerInput({
  id,
  value,
  onChange,
  placeholder = '—',
  disabled,
  className,
  minDate,
  maxDate,
}: QuickSelectDatePickerInputProps) {
  const [open, setOpen] = React.useState(false)
  const [viewMode, setViewMode] = React.useState<ViewMode>('days')
  const selected = parseCalendarDate(value) ?? undefined
  const today = React.useMemo(() => new Date(), [])

  const [viewYear, setViewYear] = React.useState(
    () => selected?.getFullYear() ?? today.getFullYear(),
  )
  const [viewMonth, setViewMonth] = React.useState(() => selected?.getMonth() ?? today.getMonth())
  const [yearGridAnchor, setYearGridAnchor] = React.useState(
    () =>
      Math.floor((selected?.getFullYear() ?? today.getFullYear()) / YEAR_GRID_SIZE) *
      YEAR_GRID_SIZE,
  )

  /* eslint-disable react-hooks/exhaustive-deps */
  React.useEffect(() => {
    if (open) {
      const base = selected ?? today
      setViewYear(base.getFullYear())
      setViewMonth(base.getMonth())
      setYearGridAnchor(Math.floor(base.getFullYear() / YEAR_GRID_SIZE) * YEAR_GRID_SIZE)
      setViewMode('days')
    }
  }, [open])
  /* eslint-enable react-hooks/exhaustive-deps */

  const handleSelect = (date: Date) => {
    const isOutOfRange = (minDate && date < minDate) || (maxDate && date > maxDate)
    if (isOutOfRange) return
    onChange(toCalendarDateString(date))
    setOpen(false)
  }

  const isMonthDisabled = (month: number) => {
    const monthStart = new Date(viewYear, month, 1)
    const monthEnd = new Date(viewYear, month + 1, 0)
    return Boolean((maxDate && monthStart > maxDate) || (minDate && monthEnd < minDate))
  }

  const isYearDisabled = (year: number) => {
    const yearStart = new Date(year, 0, 1)
    const yearEnd = new Date(year, 11, 31)
    return Boolean((maxDate && yearStart > maxDate) || (minDate && yearEnd < minDate))
  }

  const handlePrev = () => {
    if (viewMode === 'days') {
      if (viewMonth === 0) {
        setViewMonth(11)
        setViewYear((y) => y - 1)
      } else setViewMonth((m) => m - 1)
    } else if (viewMode === 'months') {
      setViewYear((y) => y - 1)
    } else {
      setYearGridAnchor((a) => a - YEAR_GRID_SIZE)
    }
  }

  const handleNext = () => {
    if (viewMode === 'days') {
      if (viewMonth === 11) {
        setViewMonth(0)
        setViewYear((y) => y + 1)
      } else setViewMonth((m) => m + 1)
    } else if (viewMode === 'months') {
      setViewYear((y) => y + 1)
    } else {
      setYearGridAnchor((a) => a + YEAR_GRID_SIZE)
    }
  }

  const cycleViewMode = () =>
    setViewMode((current) =>
      current === 'days' ? 'months' : current === 'months' ? 'years' : 'days',
    )

  const headerLabel =
    viewMode === 'years'
      ? `${yearGridAnchor} – ${yearGridAnchor + YEAR_GRID_SIZE - 1}`
      : viewMode === 'months'
        ? String(viewYear)
        : `${MONTHS_FULL[viewMonth]} ${viewYear}`

  const formatted = value ? formatCalendarDate(value) : null

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          id={id}
          type="button"
          variant="outline"
          disabled={disabled}
          className={cn(
            'h-9 w-full justify-start text-left font-normal',
            !selected && 'text-muted-foreground',
            className,
          )}
        >
          <CalendarIcon className="mr-2 h-4 w-4 shrink-0 opacity-50" />
          {formatted ?? placeholder}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-3" align="start">
        <div className="space-y-3">
          <div className="flex items-center justify-between gap-2">
            <Button
              type="button"
              variant="ghost"
              size="icon"
              className="h-7 w-7"
              onClick={handlePrev}
              aria-label="Previous"
            >
              <ChevronLeft className="h-4 w-4" />
            </Button>
            <button
              type="button"
              onClick={cycleViewMode}
              className="rounded px-1 text-sm font-medium hover:bg-accent"
              aria-label="Select month or year"
            >
              {headerLabel}
            </button>
            <Button
              type="button"
              variant="ghost"
              size="icon"
              className="h-7 w-7"
              onClick={handleNext}
              aria-label="Next"
            >
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>

          {viewMode === 'days' && (
            <CalendarGrid
              viewYear={viewYear}
              viewMonth={viewMonth}
              selected={selected}
              today={today}
              minDate={minDate}
              maxDate={maxDate}
              onSelect={handleSelect}
            />
          )}

          {viewMode === 'months' && (
            <div className="grid grid-cols-3 gap-1">
              {MONTHS_SHORT.map((label, index) => {
                const disabled = isMonthDisabled(index)
                return (
                  <button
                    key={label}
                    type="button"
                    disabled={disabled}
                    onClick={() => {
                      setViewMonth(index)
                      setViewMode('days')
                    }}
                    className={cn(
                      'rounded px-2 py-1.5 text-sm hover:bg-accent',
                      index === viewMonth &&
                        'bg-primary text-primary-foreground hover:bg-primary/90',
                      disabled && 'opacity-30 cursor-not-allowed hover:bg-transparent',
                    )}
                  >
                    {label}
                  </button>
                )
              })}
            </div>
          )}

          {viewMode === 'years' && (
            <div className="grid grid-cols-3 gap-1">
              {Array.from({ length: YEAR_GRID_SIZE }, (_, i) => yearGridAnchor + i).map((year) => {
                const disabled = isYearDisabled(year)
                return (
                  <button
                    key={year}
                    type="button"
                    disabled={disabled}
                    onClick={() => {
                      setViewYear(year)
                      setViewMode('months')
                    }}
                    className={cn(
                      'rounded px-2 py-1.5 text-sm hover:bg-accent',
                      year === viewYear && 'bg-primary text-primary-foreground hover:bg-primary/90',
                      disabled && 'opacity-30 cursor-not-allowed hover:bg-transparent',
                    )}
                  >
                    {year}
                  </button>
                )
              })}
            </div>
          )}

          <div className="flex justify-between border-t pt-2">
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => {
                onChange('')
                setOpen(false)
              }}
            >
              Clear
            </Button>
            <Button type="button" variant="ghost" size="sm" onClick={() => handleSelect(today)}>
              Today
            </Button>
          </div>
        </div>
      </PopoverContent>
    </Popover>
  )
}
